import { useEffect } from 'react';
import { useStickToBottom } from 'use-stick-to-bottom';

/**
 * @desc Chat transcript scrolling: stick to the bottom while a turn arrives, release the moment
 * the reader scrolls up.
 *
 * This replaces an effect that set `viewport.scrollTop = viewport.scrollHeight` on every change
 * to a hand-counted "flat length" of the groups. That had two faults the counter could not fix.
 * It had no notion of the reader: a user who had scrolled up to re-read an earlier answer was
 * yanked back down by the next delta. And it only fired when the count changed, so growth that
 * did not change the count — an answer's markdown arriving, an image loading, a table
 * reflowing — did not scroll at all, which is exactly the growth that happens while the user is
 * watching.
 *
 * `use-stick-to-bottom` (MIT, React 19 in peer range) watches the content with a
 * `ResizeObserver` instead of counting anything, so it follows real height and distinguishes
 * the user's own scrolling from its own animation. `resize: 'smooth'` is its velocity-based
 * spring rather than an easing curve with a fixed duration — the right shape when the content
 * being followed changes size unpredictably.
 */
export function useChatScroll(conversationId: string, isEmpty: boolean) {
  const { scrollRef, contentRef, isAtBottom, scrollToBottom, stopScroll } = useStickToBottom({
    // No visible travel on first paint: opening a chat already scrolled to the end is not a
    // scroll the reader should have to watch.
    initial: 'instant',
    resize: 'smooth',
  });

  useEffect(() => {
    // An empty transcript has no bottom worth pinning to: what fills it is the first-run mode
    // picker (or an empty-state line), read from the top. On a phone the picker is taller than
    // the viewport, and sticking to the bottom scrolled its first card out of view (H2-238).
    // Releasing the lock also stops the picker's own growth from dragging the view down.
    if (isEmpty) {
      stopScroll();
      return;
    }
    // Opening a different conversation is a fresh read, not a continuation, so whatever the
    // reader had escaped to in the last one must not carry over — hence `ignoreEscapes`. The
    // same holds when the first message lands in an empty one: it re-engages the lock.
    void scrollToBottom({ animation: 'instant', ignoreEscapes: true });
  }, [conversationId, isEmpty, scrollToBottom, stopScroll]);

  return { scrollRef, contentRef, isAtBottom };
}
